import { useEffect, useRef } from 'react';
import { Button } from '@/shared/ui/Button';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Diálogo de confirmação sobre `<dialog>` nativo.
 *
 * O elemento nativo entrega de graça o que costuma ser reimplementado errado:
 * focus trap, fechamento por Esc, backdrop inerte e semântica de modal.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancelar',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // `cancel` cobre Esc e o fechamento nativo; durante o envio, ignoramos.
    const handleCancel = (event: Event) => {
      event.preventDefault();
      if (!loading) onCancel();
    };

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [loading, onCancel]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      className="m-auto w-[min(28rem,calc(100vw-2rem))] rounded-card border border-line bg-surface p-0 shadow-elevated backdrop:bg-ink/40 backdrop:backdrop-blur-[2px] open:animate-scale-in"
    >
      <div className="space-y-2 px-6 pt-6">
        <h2 id="confirm-dialog-title" className="text-base font-semibold text-ink">
          {title}
        </h2>
        <p id="confirm-dialog-description" className="text-sm text-ink-muted">
          {description}
        </p>
      </div>

      <div className="mt-6 flex justify-end gap-2 border-t border-line bg-surface-muted/60 px-6 py-4">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button size="sm" onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </div>
    </dialog>
  );
}
